// Copyright 2025, Colin McKee
// SPDX-License-Identifier: Apache-2.0
package io.github.fletchmckee.liquid.samples.app.demos.drag

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.systemBarsPadding
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LocalRippleConfiguration
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.adaptive.currentWindowAdaptiveInfo
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.dropShadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import androidx.window.core.layout.WindowSizeClass
import io.github.fletchmckee.liquid.LiquidState
import io.github.fletchmckee.liquid.liquefiable
import io.github.fletchmckee.liquid.liquid
import io.github.fletchmckee.liquid.rememberLiquidState
import io.github.fletchmckee.liquid.samples.app.common.LiquidControls
import io.github.fletchmckee.liquid.samples.app.common.retainLiquidScopeManager
import io.github.fletchmckee.liquid.samples.app.displayNavIcons
import io.github.fletchmckee.liquid.samples.app.nodes.testTagsAsResourceId
import io.github.fletchmckee.liquid.samples.app.theme.LiquidShadow
import io.github.fletchmckee.liquid.samples.app.theme.LocalInitialDispersion
import io.github.fletchmckee.liquid.samples.app.theme.LocalInitialFrost
import io.github.fletchmckee.liquid.samples.app.theme.LocalUseLiquid
import io.github.fletchmckee.liquid.samples.app.utils.thenIf
import liquid_root.samples.shared.generated.resources.Res
import liquid_root.samples.shared.generated.resources.moon_and_stars
import liquid_root.samples.shared.generated.resources.northern_lights
import liquid_root.samples.shared.generated.resources.ny_city
import org.jetbrains.compose.resources.painterResource

@Composable
fun LiquidDraggableScreen(
  modifier: Modifier = Modifier,
  liquidState: LiquidState = rememberLiquidState(),
  navController: NavController = rememberNavController(),
  sliderContainerColor: Color = MaterialTheme.colorScheme.surface,
  windowSizeClass: WindowSizeClass = currentWindowAdaptiveInfo().windowSizeClass,
) = CompositionLocalProvider(LocalRippleConfiguration provides null) {
  val initialFrost = LocalInitialFrost.current
  val initialDispersion = LocalInitialDispersion.current
  val isLandscape = windowSizeClass.isWidthAtLeastBreakpoint(WindowSizeClass.WIDTH_DP_MEDIUM_LOWER_BOUND)

  val liquidScopeManager = retainLiquidScopeManager(
    initialFrost = initialFrost,
    initialDispersion = initialDispersion,
    initialEdge = 0.05f,
  )

  var showSliders by rememberSaveable { mutableStateOf(true) }

  Box(modifier) {
    // Content layer
    PagerBackground(liquidState)

    // Controls layer
    SettingsButtons(
      liquidState = liquidState,
      isLandscape = isLandscape,
      onBackClick = { navController.popBackStack() },
      onControlsClick = { showSliders = !showSliders },
      containerColor = sliderContainerColor,
      modifier = Modifier
        .systemBarsPadding()
        .padding(24.dp)
        .zIndex(3f),
    )

    LiquidControls(
      liquidState = liquidState,
      showSliders = showSliders,
      liquidScopeManager = liquidScopeManager,
      containerColor = sliderContainerColor,
      isLandscape = isLandscape,
    )

    LiquidDraggableBox(
      liquidState = liquidState,
      liquidScopeManager = liquidScopeManager,
      initialYOffset = if (isLandscape) 0.dp else (-150).dp,
    )
  }
}

@Composable
private fun PagerBackground(
  liquidState: LiquidState,
  pagerState: PagerState = rememberPagerState { 3 },
) = HorizontalPager(
  state = pagerState,
  modifier = Modifier
    .fillMaxSize()
    .thenIf(LocalUseLiquid.current) {
      liquefiable(liquidState)
    },
) { page ->
  val drawable = when (page) {
    0 -> painterResource(Res.drawable.moon_and_stars)
    1 -> painterResource(Res.drawable.northern_lights)
    else -> painterResource(Res.drawable.ny_city)
  }
  Image(
    painter = drawable,
    contentDescription = null,
    contentScale = ContentScale.Crop,
    modifier = Modifier.fillMaxSize(),
  )
}

@Composable
private fun SettingsButtons(
  liquidState: LiquidState,
  isLandscape: Boolean,
  onBackClick: () -> Unit,
  onControlsClick: () -> Unit,
  containerColor: Color,
  modifier: Modifier = Modifier,
) = when {
  isLandscape -> Column(
    modifier = modifier,
    verticalArrangement = Arrangement.spacedBy(16.dp),
  ) {
    BackButton(
      liquidState = liquidState,
      onClick = onBackClick,
      containerColor = containerColor,
    )

    ControlsButton(
      liquidState = liquidState,
      onClick = onControlsClick,
      containerColor = containerColor,
    )
  }

  else -> Row(modifier) {
    BackButton(
      liquidState = liquidState,
      onClick = onBackClick,
      containerColor = containerColor,
    )

    Spacer(Modifier.weight(1f))

    ControlsButton(
      liquidState = liquidState,
      onClick = onControlsClick,
      containerColor = containerColor,
    )
  }
}

@Composable
private fun BackButton(
  liquidState: LiquidState,
  onClick: () -> Unit,
  containerColor: Color,
  modifier: Modifier = Modifier,
) {
  if (displayNavIcons()) {
    IconButton(
      modifier = modifier
        .zIndex(3f)
        .dropShadow(CircleShape, LiquidShadow)
        .thenIf(LocalUseLiquid.current) {
          liquid(liquidState) {
            frost = 10.dp
            shape = CircleShape
            tint = containerColor
            edge = 0.05f
          }
        },
      onClick = onClick,
    ) {
      Icon(
        imageVector = Icons.AutoMirrored.Default.ArrowBack,
        contentDescription = "Back button",
        tint = MaterialTheme.colorScheme.onBackground,
      )
    }
  }
}

@Composable
private fun ControlsButton(
  liquidState: LiquidState,
  onClick: () -> Unit,
  containerColor: Color,
  modifier: Modifier = Modifier,
) = IconButton(
  modifier = modifier
    .dropShadow(CircleShape, LiquidShadow)
    .thenIf(LocalUseLiquid.current) {
      liquid(liquidState) {
        frost = 10.dp
        shape = CircleShape
        tint = containerColor
        edge = 0.05f
      }
    }
    .testTag("settingsButton")
    .testTagsAsResourceId(true),
  onClick = onClick,
) {
  Icon(
    imageVector = Icons.Default.Menu,
    contentDescription = "Sliders visibility button",
    tint = MaterialTheme.colorScheme.onBackground,
  )
}
